import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  IconButton,
  Divider,
  Paper,
  FormControl,
  InputLabel,
  Select,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CallSplit,
} from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  conditionGroup: {
    border: "1px solid #e0e0e0",
    borderRadius: "8px",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: "#fafafa",
    position: "relative",
  },
  subCondition: {
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: "4px",
    border: "1px dashed #ccc",
  },
  conditionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#f3e5f5",
    borderRadius: "4px",
  },
  operatorChip: {
    padding: "2px 8px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: "bold",
    marginRight: theme.spacing(1),
  },
  andChip: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
  },
  orChip: {
    backgroundColor: "#fff3e0",
    color: "#f57c00",
  },
  valueTypeSelect: {
    minWidth: 100,
  },
}));

const operators = [
  { value: "equals_to", label: "Igual a" },
  { value: "not_equals_to", label: "Diferente de" },
  { value: "contains", label: "Contém" },
  { value: "not_contains", label: "Não contém" },
  { value: "greater_than", label: "Maior que" },
  { value: "less_than", label: "Menor que" },
  { value: "greater_than_or_equals", label: "Maior ou igual a" },
  { value: "less_than_or_equals", label: "Menor ou igual a" },
  { value: "starts_with", label: "Começa com" },
  { value: "ends_with", label: "Termina com" },
  { value: "is_empty", label: "Está vazio" },
  { value: "is_not_empty", label: "Não está vazio" },
  { value: "is_true", label: "É verdadeiro" },
  { value: "is_false", label: "É falso" },
];

const valueTypes = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "boolean", label: "Booleano" },
];

const initialCondition = {
  variable: "",
  operator: "equals_to",
  value: "",
  valueType: "text",
  andConditions: [],
  orConditions: [],
};

const FlowBuilderAddIfNodeModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close,
}) => {
  const classes = useStyles();
  const [conditions, setConditions] = useState([{ ...initialCondition }]);

  useEffect(() => {
    if (open === "edit" && data?.data?.conditions) {
      // Garante que valueType existe nas condições antigas
      const conditionsWithType = data.data.conditions.map(c => ({
        ...c,
        valueType: c.valueType || "text",
        andConditions: (c.andConditions || []).map(sub => ({
          ...sub,
          valueType: sub.valueType || "text"
        })),
        orConditions: (c.orConditions || []).map(sub => ({
          ...sub,
          valueType: sub.valueType || "text"
        })),
      }));
      setConditions(conditionsWithType);
    } else if (open === "create") {
      setConditions([{ ...initialCondition }]);
    }
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setConditions([{ ...initialCondition }]);
  };

  const handleSave = () => {
    // Validação básica
    const noValueOperators = ["is_empty", "is_not_empty", "is_true", "is_false"];
    const isValid = conditions.every(
      (c) => c.variable && (noValueOperators.includes(c.operator) || c.value)
    );

    if (!isValid) {
      alert("Por favor, preencha todos os campos das condições");
      return;
    }

    const formData = { conditions };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: formData,
      });
    } else {
      onSave(formData);
    }
    handleClose();
  };

  const addCondition = () => {
    setConditions([...conditions, { ...initialCondition }]);
  };

  const removeCondition = (index) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index));
    }
  };

  const updateCondition = (index, field, value) => {
    const newConditions = [...conditions];
    newConditions[index][field] = value;

    // Auto-ajustar operador quando muda tipo
    if (field === "valueType") {
      if (value === "boolean") {
        newConditions[index].operator = "is_true";
        newConditions[index].value = "";
      } else if (newConditions[index].operator === "is_true" || newConditions[index].operator === "is_false") {
        newConditions[index].operator = "equals_to";
      }
    }

    setConditions(newConditions);
  };

  const addSubCondition = (conditionIndex, type) => {
    const newConditions = [...conditions];
    const subConditionKey = type === "and" ? "andConditions" : "orConditions";
    newConditions[conditionIndex][subConditionKey] = [
      ...(newConditions[conditionIndex][subConditionKey] || []),
      { variable: "", operator: "equals_to", value: "", valueType: "text" },
    ];
    setConditions(newConditions);
  };

  const removeSubCondition = (conditionIndex, subIndex, type) => {
    const newConditions = [...conditions];
    const subConditionKey = type === "and" ? "andConditions" : "orConditions";
    newConditions[conditionIndex][subConditionKey] = newConditions[conditionIndex][
      subConditionKey
    ].filter((_, i) => i !== subIndex);
    setConditions(newConditions);
  };

  const updateSubCondition = (conditionIndex, subIndex, type, field, value) => {
    const newConditions = [...conditions];
    const subConditionKey = type === "and" ? "andConditions" : "orConditions";
    newConditions[conditionIndex][subConditionKey][subIndex][field] = value;

    // Auto-ajustar operador quando muda tipo
    if (field === "valueType") {
      if (value === "boolean") {
        newConditions[conditionIndex][subConditionKey][subIndex].operator = "is_true";
        newConditions[conditionIndex][subConditionKey][subIndex].value = "";
      } else if (
        newConditions[conditionIndex][subConditionKey][subIndex].operator === "is_true" ||
        newConditions[conditionIndex][subConditionKey][subIndex].operator === "is_false"
      ) {
        newConditions[conditionIndex][subConditionKey][subIndex].operator = "equals_to";
      }
    }

    setConditions(newConditions);
  };

  // Filtra operadores baseado no tipo de valor
  const getOperatorsForType = (valueType) => {
    if (valueType === "boolean") {
      return operators.filter(op =>
        ["is_true", "is_false", "equals_to", "not_equals_to"].includes(op.value)
      );
    }
    if (valueType === "number") {
      return operators.filter(op =>
        !["contains", "not_contains", "starts_with", "ends_with", "is_true", "is_false"].includes(op.value)
      );
    }
    // text - todos exceto is_true/is_false
    return operators.filter(op =>
      !["is_true", "is_false"].includes(op.value)
    );
  };

  const renderConditionInputs = (condition, onChange, showValueField = true) => {
    const noValueOperators = ["is_empty", "is_not_empty", "is_true", "is_false"];
    const showValue = showValueField && !noValueOperators.includes(condition.operator);
    const availableOperators = getOperatorsForType(condition.valueType || "text");

    return (
      <Grid container spacing={2} alignItems="center">
        {/* Tipo de Valor */}
        <Grid item xs={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Tipo"
            value={condition.valueType || "text"}
            onChange={(e) => onChange("valueType", e.target.value)}
            variant="outlined"
          >
            {valueTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Variável */}
        <Grid item xs={showValue ? 3 : 4}>
          <TextField
            fullWidth
            size="small"
            label="Variável"
            value={condition.variable}
            onChange={(e) => onChange("variable", e.target.value)}
            variant="outlined"
            placeholder="${variavel}"
            helperText="Ex: ${nome}, ${isFromMe}"
          />
        </Grid>

        {/* Operador */}
        <Grid item xs={showValue ? 3 : 4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Operador"
            value={condition.operator}
            onChange={(e) => onChange("operator", e.target.value)}
            variant="outlined"
          >
            {availableOperators.map((op) => (
              <MenuItem key={op.value} value={op.value}>
                {op.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Valor */}
        {showValue && (
          <Grid item xs={4}>
            {condition.valueType === "boolean" ? (
              <TextField
                select
                fullWidth
                size="small"
                label="Valor"
                value={condition.value}
                onChange={(e) => onChange("value", e.target.value)}
                variant="outlined"
              >
                <MenuItem value="true">Verdadeiro (true)</MenuItem>
                <MenuItem value="false">Falso (false)</MenuItem>
              </TextField>
            ) : condition.valueType === "number" ? (
              <TextField
                fullWidth
                size="small"
                label="Valor"
                type="number"
                value={condition.value}
                onChange={(e) => onChange("value", e.target.value)}
                variant="outlined"
                placeholder="0"
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Valor"
                value={condition.value}
                onChange={(e) => onChange("value", e.target.value)}
                variant="outlined"
                placeholder="Valor ou ${var}"
              />
            )}
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Dialog open={!!open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CallSplit style={{ color: "#9c27b0" }} />
          <Typography variant="h6">
            {open === "edit" ? "Editar Condição IF" : "Nova Condição IF"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ maxHeight: "60vh", overflowY: "auto" }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Configure múltiplas condições. Cada condição pode ter subcondições AND
          e OR. O fluxo seguirá pela primeira condição verdadeira.
        </Typography>

        <Box mt={2}>
          {conditions.map((condition, index) => (
            <Paper key={index} className={classes.conditionGroup} elevation={1}>
              {/* Header da condição */}
              <div className={classes.conditionHeader}>
                <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                  Condição {index + 1}
                </Typography>
                {conditions.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeCondition(index)}
                    style={{ color: "#f44336" }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </div>

              {/* Inputs da condição principal */}
              {renderConditionInputs(condition, (field, value) =>
                updateCondition(index, field, value)
              )}

              {/* Subcondições AND */}
              {condition.andConditions?.map((subCondition, subIdx) => (
                <div key={`and_${subIdx}`} className={classes.subCondition}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <span className={`${classes.operatorChip} ${classes.andChip}`}>
                      AND
                    </span>
                    <Box flexGrow={1} />
                    <IconButton
                      size="small"
                      onClick={() => removeSubCondition(index, subIdx, "and")}
                      style={{ color: "#f44336" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {renderConditionInputs(subCondition, (field, value) =>
                    updateSubCondition(index, subIdx, "and", field, value)
                  )}
                </div>
              ))}

              {/* Subcondições OR */}
              {condition.orConditions?.map((subCondition, subIdx) => (
                <div key={`or_${subIdx}`} className={classes.subCondition}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <span className={`${classes.operatorChip} ${classes.orChip}`}>
                      OR
                    </span>
                    <Box flexGrow={1} />
                    <IconButton
                      size="small"
                      onClick={() => removeSubCondition(index, subIdx, "or")}
                      style={{ color: "#f44336" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {renderConditionInputs(subCondition, (field, value) =>
                    updateSubCondition(index, subIdx, "or", field, value)
                  )}
                </div>
              ))}

              {/* Botões para adicionar subcondições */}
              <Box display="flex" justifyContent="center" mt={2} gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addSubCondition(index, "and")}
                  style={{
                    borderColor: "#1976d2",
                    color: "#1976d2",
                    marginRight: "8px",
                  }}
                >
                  AND
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => addSubCondition(index, "or")}
                  style={{ borderColor: "#f57c00", color: "#f57c00" }}
                >
                  OR
                </Button>
              </Box>
            </Paper>
          ))}

          {/* Botão para adicionar nova condição */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addCondition}
            style={{
              borderColor: "#9c27b0",
              color: "#9c27b0",
              marginTop: "8px",
            }}
          >
            Adicionar Nova Condição (novo caminho)
          </Button>
        </Box>

        <Divider style={{ margin: "16px 0" }} />

        <Typography variant="body2" color="textSecondary">
          <strong>Dica:</strong> Cada condição cria uma saída separada no nó. A
          condição "Senão" (else) é usada quando nenhuma condição for verdadeira.
          <br />
          <strong>Booleano:</strong> Use os operadores "É verdadeiro" ou "É falso" para variáveis como {`\${isFromMe}`}.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          style={{ backgroundColor: "#9c27b0" }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderAddIfNodeModal;
